import React, { useEffect, useState } from "react";
import CreatableSelect from "react-select/creatable";
import { components } from "react-select";
import { Barn, Trainer } from "../../models";
import Spinner from "../Spinners/Spinner";
import { IonCol } from "@ionic/react";
import ErrorAlert from "../Errors/ErrorAlert";
import VerifyTrainerModal from "./VerifyTrainerModal";
import { formattedOption } from "../../utilities/trainer/SelectOrCreateTrainerUtils";
const { v4: uuidv4 } = require("uuid");

interface SelectOrCreateTrainerProps {
    userTrainerOptions?: formattedOption[] | null | undefined;
    barnTrainerOptions?: formattedOption[] | null | undefined;

    selectedBarn?: Barn | null | undefined;
    selectedValue?: string | null;
    onSelect: (trainer?: Trainer, trainerName?: string) => void;
    isHeightRequired?: boolean;
    height?: string;
}

/**
 * This function is passed into the select component to define what should be shown when an option is selected
 * Instead of showing the 3 lines from the option,
 * it will only show the 1 line defined below: trainerName
 * @param props
 * @returns
 */
const SingleValue = (props: any) => <components.SingleValue {...props}>{props.data.trainerName || props.data.label}</components.SingleValue>;

const SelectOrCreateTrainer: React.FC<SelectOrCreateTrainerProps> = ({
    userTrainerOptions,
    barnTrainerOptions,
    selectedBarn,
    selectedValue,
    onSelect,
    isHeightRequired,
    height,
}) => {
    // Dropdown Section Titles
    const createdByUserGroupLabel = "Trainers Created by You";
    const barnTrainersGroupLabel = selectedBarn ? selectedBarn.name + " Trainers" : "Barn Trainers";

    // Groups - each group represents a section in the drop down
    const barnTrainersGroup = {
        label: barnTrainersGroupLabel,
        options:
            barnTrainerOptions && barnTrainerOptions.length > 0
                ? barnTrainerOptions
                : [{ value: selectedBarn ? `No trainers are in ${selectedBarn?.name}` : `No barn selected.`, label: " ", trainerName: " " }],
    };

    const trainersCreatedByUserGroup = {
        label: createdByUserGroupLabel,
        options: userTrainerOptions && userTrainerOptions.length > 0 ? userTrainerOptions : [{ value: "You have not created any trainers yet.", label: "", trainerName: "" }],
    };

    // General State
    const [currentValue, setCurrentValue] = useState<formattedOption | null | undefined>();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const findValueInList = (value: string) => {
        // Set up the options for arrays of trainers that should be checked
        const optionArray = (userTrainerOptions || [])?.concat(barnTrainerOptions || []);
        // If there are options, loop through them and set the current value
        if (optionArray) {
            for (var i = 0; i < optionArray.length; i++) {
                const currentOption = optionArray[i];
                if (currentOption.value === value) {
                    setCurrentValue(currentOption);
                }
            }
        }
    };

    if (selectedValue && !currentValue) {
        findValueInList(selectedValue);
    }

    useEffect(() => {
        if (selectedValue) {
            const optionArray = (userTrainerOptions || []).concat(barnTrainerOptions || []);
            const match = optionArray.find((o) => o.value === selectedValue);
            if (match) {
                setCurrentValue(match);
            }
        } else {
            setCurrentValue(null);
        }
    }, [selectedValue, userTrainerOptions, barnTrainerOptions]);

    // DROPDOWN Functions
    const handleOnChange = async (event?: any) => {
        // Trainer was selected from either the barnTrainers or trainersCreatedByUser groups
        if (event && event.trainer) {
            await setCurrentValue(event);
            onSelect(event.trainer);
        }

        // Option from the dropdown was created by the current user with only the trainer name
        else if (event && event.trainerName && event.trainerName !== " ") {
            const newFormattedOption = handleCreateNewFormattedOptionWithJustTrainerName(event.trainerNameLabel);
            await setCurrentValue(newFormattedOption);
            onSelect(undefined, event.trainerName);
        }

        // Option from the dropdown was that there are no trainers in this section
        else if (event && event.trainerName && event.trainerName === " ") {
            // Open the modal and have the user type in a name and verify the trainer.
            setIsModalVisible(true);
        }

        // Here - event.value will be the value the user typed into the CreateableSelect field
        else if (event && event.value) {
            // Set the currentValue in state so that it can be used inside of the modal.
            const newFormattedOption = handleCreateNewFormattedOptionWithJustTrainerName(event.value);
            await setCurrentValue(newFormattedOption);

            // Verify Trainer - If the trainer was not in the barnTrainers or trainersCreatedByUser groups
            setIsModalVisible(true);
        }

        // User clicked the X button to clear the value.
        else {
            await setCurrentValue(null);
            onSelect(undefined);
        }
    };

    const handleCreateNewFormattedOptionWithJustTrainerName = (trainerName: string) => {
        const newFormattedOption: formattedOption = {
            value: uuidv4(),
            label: trainerName + " " + (selectedBarn?.name || ""),
            trainerName: trainerName,
            trainerNameLabel: trainerName,
            barnLabel: selectedBarn?.name || "",
            trainer: undefined,
        };
        return newFormattedOption;
    };

    /**
     * User found the trainer by name in the RSP Database
     * Send that trainer info back to the parent component
     * And close the modal
     */
    const handleUseMatchedTrainer = async (event: formattedOption) => {
        if (event.trainerName) {
            await setCurrentValue(event);
            onSelect(event.trainer, event.trainerName);
            setIsModalVisible(false);
        } else {
            setError("No trainer name was found.");
        }
    };
    /**
     * User did not find the trainer by name in the RSP Database
     * Send that trainer name back to the parent component in order to create a new trainer
     * And close the modal
     */
    const handleCreateNewTrainer = (newTrainerName: string) => {
        onSelect(undefined, newTrainerName);
        setIsModalVisible(false);
    };
    /**
     * User did not find the trainer by name in the RSP Database.
     * Assume the user thought the trainer existed and realized there is a typo in the trainer name.
     * Send the user back to the starting point to try again.
     */
    const handleCancel = () => {
        onSelect(undefined);
        setIsModalVisible(false);
        setCurrentValue(null);
    };

    return (
        <>
            {isLoading ? (
                <Spinner />
            ) : (
                <IonCol size="10">
                    {error && <ErrorAlert error={error} width="12" />}
                    <CreatableSelect
                        id="select-or-create-trainer"
                        inputId="select-or-create-trainer-input"
                        placeholder="Type the trainer's name"
                        styles={{
                            // Fixes the overlapping problem of the component
                            control: (baseStyles) => (isHeightRequired ? { ...baseStyles, height } : { ...baseStyles }),
                            menu: (provided) => ({ ...provided, zIndex: 9999 }),
                            group: (base, state) => ({
                                ...base,
                                // Group background color
                                backgroundColor: state.label === barnTrainersGroupLabel ? "var(--ion-color-info)" : "var(--ion-color-secondary)",
                                padding: "10px",
                            }),
                            groupHeading: (base, state) => ({
                                ...base,
                                fontSize: "1em",
                                fontWeight: "bold",
                                // Section title color
                                color: "var(--ion-color-white)",
                            }),
                            option: (base, { isFocused, isSelected }) => ({
                                ...base,
                                // Hover vs. no hover colors for trainers in list
                                backgroundColor: isFocused || isSelected ? "var(--ion-color-light)" : "var(--ion-color-white)",
                                cursor: "pointer",
                                color: "var(--ion-color-primary)",
                            }),
                        }}
                        defaultValue={currentValue}
                        value={currentValue}
                        menuPortalTarget={document.body}
                        menuPlacement={"auto"}
                        isClearable
                        options={[barnTrainersGroup, trainersCreatedByUserGroup]}
                        noOptionsMessage={({ inputValue }) => (!inputValue ? "No input found" : "No results found")}
                        formatOptionLabel={(option: any) =>
                            option.trainerNameLabel ? (
                                <div className="px-10">
                                    <div className="font-weight-bold text-default">{option.trainerNameLabel}</div>
                                    {barnTrainersGroup.options.includes(option) ? (
                                        // Display for barnTrainersGroup
                                        <div className="display-5 text-medium">
                                            <div>Created By: {option.createdByLabel || "Unknown"}</div>
                                            <div>Location: {option.locationLabel || "Unknown"}</div>
                                        </div>
                                    ) : trainersCreatedByUserGroup.options.includes(option) ? (
                                        // Display for trainersCreatedByUserGroup
                                        <div className="display-5 text-medium">
                                            <div>Barn: {option.barnLabel || "Unknown"}</div>
                                            <div>Location: {option.locationLabel || "Unknown"}</div>
                                        </div>
                                    ) : null}
                                </div>
                            ) : (
                                <div className="p-0">
                                    <div className="font-weight-bold text-default">{option.textToDisplay || "Select or Create Trainer: " + option.value}</div>
                                </div>
                            )
                        }
                        components={{ SingleValue }}
                        onChange={(event: any) => handleOnChange(event)}
                        createOptionPosition={"first"}
                    />
                </IonCol>
            )}
            {isModalVisible && (
                <VerifyTrainerModal
                    currentValue={currentValue}
                    handleCancel={() => handleCancel()}
                    handleUseMatchedTrainer={(e: formattedOption) => handleUseMatchedTrainer(e)}
                    handleCreateNewTrainer={(newTrainerName: string) => handleCreateNewTrainer(newTrainerName)}
                />
            )}
        </>
    );
};

export default SelectOrCreateTrainer;
